/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { BillingSubscriptionLevel, UserProfile, ProfileState } from '@modules/ui-types'
import type { BillingActivity, Subscription } from '@modules/ui-types/billing'

import { DEFAULT_SUBSCRIPTION_LEVEL, SUBSCRIPTION_LEVELS } from '../constants'

interface BillingSubscriptionWithHourlyRate extends Subscription {
  hourlyRate: number
}

export function getSubscriptionsWithHourlyRates({
  subscription,
  activity,
}: {
  subscription: Subscription
  activity?: BillingActivity
}): BillingSubscriptionWithHourlyRate {
  const hourlyRate = activity?.[subscription.value] || 0

  return {
    ...subscription,
    hourlyRate,
  }
}

export const getHourlyRateForLevel = ({
  subscription,
  activity,
}: {
  subscription: BillingSubscriptionLevel
  activity?: BillingActivity
}) => activity?.[subscription] || 0

export const defaultSubscriptionLevelByUser = ({
  profile,
  trialDefaultTierFlag,
}: {
  profile: ProfileState | UserProfile
  trialDefaultTierFlag?: BillingSubscriptionLevel
}): BillingSubscriptionLevel => {
  if (!profile || !profile.inTrial) {
    return DEFAULT_SUBSCRIPTION_LEVEL
  }

  if (trialDefaultTierFlag && SUBSCRIPTION_LEVELS.includes(trialDefaultTierFlag)) {
    return trialDefaultTierFlag
  }

  return DEFAULT_SUBSCRIPTION_LEVEL
}

export const getSubscriptionChangeEventType = (
  level: BillingSubscriptionLevel,
  pending_level: BillingSubscriptionLevel,
): 'upgrade' | 'downgrade' => {
  if (SUBSCRIPTION_LEVELS.indexOf(level) < SUBSCRIPTION_LEVELS.indexOf(pending_level)) {
    return 'upgrade'
  }

  return 'downgrade'
}

export const getSubscriptionQuery = (level: string | null | undefined): BillingSubscriptionLevel =>
  SUBSCRIPTION_LEVELS.find((each) => each === level) ?? DEFAULT_SUBSCRIPTION_LEVEL
